import { useState } from 'react';
import type { AdminUserRow } from '@repo/shared-types';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/ui/src/components/ui/dialog';
import { Input } from '@repo/ui/src/components/ui/input';
import { Label } from '@repo/ui/src/components/ui/label';
import { Button } from '@repo/ui/src/components/ui/button';
import { useChangeUserEmail } from '../../hooks/useAdminUsers';

interface ChangeEmailDialogProps {
  user: AdminUserRow | null;
  onOpenChange: (open: boolean) => void;
}

/** Quick 1-field form (SP1 convention: Dialog = 1-2 field form). Open state is
 *  driven by `user` being non-null so the caller doesn't need separate open/user
 *  state — closing always clears the selection via `onOpenChange(false)`. */
export function ChangeEmailDialog({ user, onOpenChange }: ChangeEmailDialogProps) {
  return (
    <Dialog open={!!user} onOpenChange={onOpenChange}>
      <DialogContent>
        {/* Keyed on the user id so switching targets remounts the form with a
            freshly-seeded input, instead of reseeding via an effect. */}
        {user && <ChangeEmailForm key={user.id} user={user} onDone={() => onOpenChange(false)} />}
      </DialogContent>
    </Dialog>
  );
}

function ChangeEmailForm({ user, onDone }: { user: AdminUserRow; onDone: () => void }) {
  const [email, setEmail] = useState(user.email);
  const changeEmail = useChangeUserEmail();

  const disabled = changeEmail.isPending || !email || email === user.email;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (disabled) return;
    changeEmail.mutate({ userId: user.id, email }, { onSuccess: onDone });
  };

  return (
    <form onSubmit={handleSubmit}>
      <DialogHeader>
        <DialogTitle>Đổi email</DialogTitle>
        <DialogDescription>
          Email mới chưa được xác thực, nên trạng thái xác thực sẽ bị gỡ. Người dùng sẽ đăng nhập
          bằng địa chỉ mới.
        </DialogDescription>
      </DialogHeader>
      <div className="py-4">
        <Label htmlFor="change-email-input">Email</Label>
        <Input
          id="change-email-input"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="mt-1.5"
          autoFocus
        />
      </div>
      <DialogFooter>
        <Button type="submit" disabled={disabled}>
          {changeEmail.isPending ? 'Đang lưu...' : 'Lưu'}
        </Button>
      </DialogFooter>
    </form>
  );
}
